/**
 * Screen-Popups (22.10.2026) – PURE Regeln, Spiegel von backend `src/core/helper/popup-rules.js`
 * (und App `popup_rules.dart`). Paritätstest: popupRules.test.ts mit denselben Fällen wie das Backend.
 */
export type PopupPlatform = 'app' | 'web';
export type PopupLayoutKind = 'card' | 'fullscreen' | 'bottom-sheet';
export type PopupFrequencyMode = 'always' | 'once' | 'once_per_session' | 'once_per_day' | 'max_n';
export type PopupAudienceWho = 'all' | 'guests' | 'users' | 'admins';
export type PopupButtonAction = 'route' | 'url' | 'close';
export type PopupImageFit = 'cover' | 'contain-blur';

export const POPUP_PLATFORMS: PopupPlatform[] = ['app', 'web'];
export const POPUP_LAYOUTS: PopupLayoutKind[] = ['card', 'fullscreen', 'bottom-sheet'];
export const POPUP_FREQUENCIES: PopupFrequencyMode[] = ['always', 'once', 'once_per_session', 'once_per_day', 'max_n'];
export const POPUP_AUDIENCES: PopupAudienceWho[] = ['all', 'guests', 'users', 'admins'];
export const POPUP_BUTTON_ACTIONS: PopupButtonAction[] = ['route', 'url', 'close'];
export const POPUP_IMAGE_ASPECT = '16:9';
export const DAY_MS = 24 * 60 * 60 * 1000;

export interface PopupButton { label: string; labelEn: string | null; action: PopupButtonAction; target: string | null }
export interface PopupImage { url: string; urlLight: string | null; alt: string | null; fit: PopupImageFit }
export interface PopupContent {
  title: string | null; titleEn: string | null;
  subtitle: string | null; subtitleEn: string | null;
  description: string | null; descriptionEn: string | null;
  images: PopupImage[];
  imageAspect: string;
  primaryButton: PopupButton | null;
  secondaryButton: PopupButton | null;
}
export interface PublicPopup {
  id: string;
  priority: number;
  platforms: PopupPlatform[];
  screens: string[];
  schedule: { from: number | null; to: number | null };
  audience: { who: PopupAudienceWho; minAppVersion: string | null };
  frequency: { mode: PopupFrequencyMode; maxShows: number | null; cooldownHours: number | null };
  trigger: { delayMs: number };
  layout: { kind: PopupLayoutKind; dismissable: boolean; showClose: boolean; backdrop: 'dark' | 'blur' };
  content: PopupContent;
  versionKey: string | null;
  active: boolean;
  createdAt: number | null;
  updatedAt: number | null;
}
export interface PopupState { shows: number; lastShownAt: number | null; dismissedAt: number | null; clickedAt?: number | null; versionKey: string | null }

const txt = (v: unknown): string => String(v ?? '').trim();
const num = (v: unknown): number => (Number.isFinite(Number(v)) ? Number(v) : 0);

export function normalizeScreenPath(path: string | null | undefined): string {
  let p = txt(path).replace(/[?#].*$/, '');
  if (!p) return '/';
  if (!p.startsWith('/')) p = `/${p}`;
  p = p.replace(/^\/en(?=\/|$)/, '');
  if (p.length > 1) p = p.replace(/\/+$/, '');
  return p || '/';
}

export function screenMatches(pattern: string | null | undefined, path: string): boolean {
  const pat = txt(pattern);
  if (!pat) return false;
  if (pat === 'all' || pat === '*') return true;
  const p = normalizeScreenPath(path);
  const normPat = normalizeScreenPath(pat);
  if (normPat === p) return true;
  const patSegs = normPat.split('/').filter(Boolean);
  const pathSegs = p.split('/').filter(Boolean);
  for (let i = 0; i < patSegs.length; i++) {
    const ps = patSegs[i];
    if (ps === '*' && i === patSegs.length - 1) return pathSegs.length >= i;
    if (i >= pathSegs.length) return false;
    if (ps === '*' || ps.startsWith(':')) continue;
    if (ps !== pathSegs[i]) return false;
  }
  return patSegs.length === pathSegs.length;
}

export const matchesAnyScreen = (screens: string[] | null | undefined, path: string): boolean => (screens ?? []).some((s) => screenMatches(s, path));

export function isScheduled(popup: Pick<PublicPopup, 'schedule'>, now = Date.now()): boolean {
  const from = popup.schedule?.from != null ? new Date(popup.schedule.from).getTime() : null;
  const to = popup.schedule?.to != null ? new Date(popup.schedule.to).getTime() : null;
  if (from != null && Number.isFinite(from) && now < from) return false;
  if (to != null && Number.isFinite(to) && now > to) return false;
  return true;
}

export function compareVersions(a: string | null | undefined, b: string | null | undefined): number {
  const pa = txt(a).split('.').map((x) => parseInt(x, 10) || 0);
  const pb = txt(b).split('.').map((x) => parseInt(x, 10) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const d = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (d !== 0) return d;
  }
  return 0;
}

export interface AudienceContext { platform?: PopupPlatform; user?: { id: string } | null; isAdmin?: boolean; appVersion?: string | null }

export function audienceMatches(popup: Pick<PublicPopup, 'platforms' | 'audience'>, { platform = 'web', user = null, isAdmin = false, appVersion = null }: AudienceContext = {}): boolean {
  if (!(popup.platforms ?? []).includes(platform)) return false;
  const who = popup.audience?.who ?? 'all';
  if (who === 'guests' && user) return false;
  if (who === 'users' && !user) return false;
  if (who === 'admins' && !isAdmin) return false;
  const minV = txt(popup.audience?.minAppVersion);
  if (minV && platform === 'app' && appVersion && compareVersions(appVersion, minV) < 0) return false;
  return true;
}

export function frequencyAllows(popup: Pick<PublicPopup, 'id' | 'frequency' | 'versionKey'>, state: PopupState | null = null, { now = Date.now(), sessionShown = new Set<string>() }: { now?: number; sessionShown?: Set<string> } = {}): boolean {
  const mode = popup.frequency?.mode ?? 'always';
  const sameVersion = !state || (state.versionKey ?? null) === (popup.versionKey ?? null);
  const s: PopupState = sameVersion && state ? state : { shows: 0, lastShownAt: null, dismissedAt: null, versionKey: null };
  if (mode === 'always') return true;
  if (mode === 'once') return num(s.shows) === 0;
  if (mode === 'once_per_session') return !sessionShown.has(popup.id);
  if (mode === 'once_per_day') return !s.lastShownAt || now - num(s.lastShownAt) >= DAY_MS;
  if (mode === 'max_n') {
    const max = Math.max(1, num(popup.frequency?.maxShows) || 1);
    if (num(s.shows) >= max) return false;
    const cooldownMs = Math.max(0, num(popup.frequency?.cooldownHours)) * 60 * 60 * 1000;
    return !s.lastShownAt || now - num(s.lastShownAt) >= cooldownMs;
  }
  return true;
}

export const byPriority = (a: PublicPopup, b: PublicPopup): number => (num(b.priority) - num(a.priority)) || (num(b.createdAt) - num(a.createdAt));

export interface PickContext extends AudienceContext { now?: number; statesById?: Record<string, PopupState>; sessionShown?: Set<string> }

export function pickPopupForScreen(popups: PublicPopup[] | null | undefined, path: string, { now = Date.now(), platform = 'web', user = null, isAdmin = false, appVersion = null, statesById = {}, sessionShown = new Set<string>() }: PickContext = {}): PublicPopup | null {
  const candidates = (popups ?? [])
    .filter((p) => p && p.active !== false)
    .filter((p) => isScheduled(p, now))
    .filter((p) => audienceMatches(p, { platform, user, isAdmin, appVersion }))
    .filter((p) => matchesAnyScreen(p.screens, path))
    .filter((p) => frequencyAllows(p, statesById[p.id] ?? null, { now, sessionShown }))
    .sort(byPriority);
  return candidates[0] ?? null;
}

export function nextState(state: PopupState | null, popup: Pick<PublicPopup, 'versionKey'>, event: 'impression' | 'dismiss' | 'click', now = Date.now()): PopupState {
  const sameVersion = !!state && (state.versionKey ?? null) === (popup.versionKey ?? null);
  const base: PopupState = sameVersion && state ? { ...state } : { shows: 0, lastShownAt: null, dismissedAt: null, clickedAt: null, versionKey: null };
  base.versionKey = popup.versionKey ?? null;
  if (event === 'impression') { base.shows = num(base.shows) + 1; base.lastShownAt = now; }
  if (event === 'dismiss') base.dismissedAt = now;
  if (event === 'click') base.clickedAt = now;
  return base;
}

/** Sprach-Fallback: EN nur wenn vorhanden, sonst DE. */
export const popupText = (de: string | null | undefined, en: string | null | undefined, lang: string): string | null => (lang === 'en' && txt(en) ? txt(en) : (txt(de) || null));

export const FREQUENCY_LABELS: Record<PopupFrequencyMode, string> = { always: 'Jedes Mal beim Betreten', once: 'Einmal und nie wieder', once_per_session: 'Einmal pro Sitzung', once_per_day: 'Einmal pro Tag', max_n: 'Maximal n-mal (mit Abkühlzeit)' };
export const LAYOUT_LABELS: Record<PopupLayoutKind, string> = { card: 'Karte', fullscreen: 'Vollbild', 'bottom-sheet': 'Bottom-Sheet' };
export const AUDIENCE_LABELS: Record<PopupAudienceWho, string> = { all: 'Alle', guests: 'Nur Gäste', users: 'Nur eingeloggte Nutzer', admins: 'Nur Admins' };
export const ACTION_LABELS: Record<PopupButtonAction, string> = { route: 'Seite in DartCloud öffnen', url: 'Externe URL', close: 'Nur schließen' };
